import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button, InputAdornment, TextField } from "@mui/material";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerDocumentRequests } from "../../api/endpoints/documents";
import { fetchPartnerMe } from "../../api/endpoints/me";
import { EmptyState, StatusChip } from "../../components/ui";
import { formatDate } from "../../utils/portalHelpers";
import { requestStage, rowProgress, sortRequestsForClient } from "./documentHelpers";
import RequestFromClientDialog from "./RequestFromClientDialog";
import UploadDocumentsDialog from "./UploadDocumentsDialog";

const STAGE_FILTERS = [
  { id: "all", label: "All" },
  { id: "ready", label: "Ready to send" },
  { id: "collecting", label: "Collecting" },
  { id: "sent", label: "Sent to office" },
  { id: "cancelled", label: "Cancelled" },
];

/** Request | client | progress | status | requested — fixed so rows line up down the list. */
const ROW_GRID =
  "md:grid md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_9rem_8.5rem_6.5rem_1.25rem] md:items-center md:gap-4";

const matchesSearch = (request, term) => {
  if (!term) return true;
  const haystack = `${request.title || ""} ${request.clientName || ""}`.toLowerCase();
  return haystack.includes(term.toLowerCase());
};

const DocumentsPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [search, setSearch] = useState("");
  const params = { page, size: 20 };

  const { data, isLoading, isError } = useQuery(
    partnerQueryKeys.documentRequests(params),
    () => fetchPartnerDocumentRequests(params),
    { keepPreviousData: true, refetchOnMount: true }
  );
  const { data: me } = useQuery(partnerQueryKeys.me(), fetchPartnerMe, {
    staleTime: 5 * 60 * 1000,
  });
  const canRaise = me?.allowIndependentDocuments !== false;

  const rows = useMemo(() => data?.content || [], [data]);

  const counts = useMemo(() => {
    const byStage = { all: rows.length };
    rows.forEach((request) => {
      const { id } = requestStage(request);
      byStage[id] = (byStage[id] || 0) + 1;
    });
    return byStage;
  }, [rows]);

  const visible = useMemo(
    () =>
      sortRequestsForClient(rows).filter(
        (request) =>
          (stageFilter === "all" || requestStage(request).id === stageFilter) &&
          matchesSearch(request, search.trim())
      ),
    [rows, stageFilter, search]
  );

  const onCreated = () => {
    queryClient.invalidateQueries(partnerQueryKeys.documentRequests());
    setPage(0);
  };

  const actions = canRaise ? (
    <div className="flex flex-wrap justify-center gap-2">
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<UploadFileOutlinedIcon />}
        onClick={() => setDialog("upload")}
      >
        Upload documents
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={<SendOutlinedIcon />}
        onClick={() => setDialog("request")}
      >
        Request from client
      </Button>
    </div>
  ) : (
    <p className="m-0 text-xs text-slate-500">
      The office sends you document requests — raising your own is switched off for your account.
    </p>
  );

  return (
    <div>
      <div className="mb-4 flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by status">
          {STAGE_FILTERS.map((option) => {
            const active = stageFilter === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStageFilter(option.id)}
                className={[
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold cursor-pointer transition-colors",
                  active
                    ? "border-teal-700 bg-teal-700 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                ].join(" ")}
              >
                {option.label}
                <span
                  className={[
                    "rounded-full px-1.5 text-[10px] tabular-nums",
                    active ? "bg-white/20" : "bg-slate-100 text-slate-500",
                  ].join(" ")}
                >
                  {counts[option.id] || 0}
                </span>
              </button>
            );
          })}
        </div>
        {actions}
      </div>

      {isError ? (
        <EmptyState
          title="Couldn't load requests"
          description="Check your connection and try again."
        />
      ) : !isLoading && rows.length === 0 ? (
        <EmptyState
          title="No documents yet"
          description="Upload documents you already have for a client, or send a client a secure link listing what you need."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <TextField
              size="small"
              placeholder="Search by client or request"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ width: { xs: "100%", sm: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div
            className={`hidden ${ROW_GRID} bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500`}
          >
            <span>Request</span>
            <span>Client</span>
            <span>Progress</span>
            <span>Status</span>
            <span>Requested</span>
            <span />
          </div>

          {visible.length === 0 ? (
            <p className="m-0 px-4 py-10 text-center text-sm text-slate-500">
              Nothing matches — try another status or search.
            </p>
          ) : (
            <ul className="list-none m-0 p-0 divide-y divide-slate-100">
              {visible.map((request) => {
                const progress = rowProgress(request);
                const stage = requestStage(request);
                const complete = progress.required > 0 && progress.resolved === progress.required;
                // Closed rounds with office-removed files read complete (3/3), not short.
                const doneCount =
                  request.status !== "ACTIVE" && progress.removed > 0
                    ? progress.resolved
                    : progress.fulfilled;
                return (
                  <li key={request.id}>
                    <Link
                      to={`/partner/documents/${request.id}`}
                      className={`group ${ROW_GRID} flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 no-underline text-inherit hover:bg-slate-50 transition-colors ${
                        stage.id === "ready" ? "shadow-[inset_3px_0_0_0_#fbbf24]" : ""
                      }`}
                    >
                      <span className="min-w-0 basis-full md:basis-auto">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className="block text-sm font-semibold text-slate-900 truncate group-hover:text-teal-800">
                            {request.title}
                          </span>
                          {request.parentRequestId ? (
                            <StatusChip tone="rose">Reupload</StatusChip>
                          ) : null}
                        </span>
                        {request.targetModuleType ? (
                          <span className="block text-xs text-slate-500 truncate">
                            Files into {request.targetModuleType}
                          </span>
                        ) : null}
                      </span>

                      <span className="min-w-0 text-sm text-slate-600 truncate">
                        {request.clientName || "—"}
                      </span>

                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-slate-100 flex">
                          <span
                            className={`block h-full ${
                              complete ? "bg-emerald-500" : "bg-teal-500"
                            }`}
                            style={{ width: `${progress.receivedPercent}%` }}
                          />
                          <span
                            className="block h-full bg-amber-400"
                            style={{ width: `${progress.removedPercent}%` }}
                          />
                        </span>
                        <span className="text-xs tabular-nums text-slate-500 whitespace-nowrap">
                          {doneCount}/{progress.required}
                        </span>
                      </span>

                      <span>
                        <StatusChip tone={stage.tone}>{stage.label}</StatusChip>
                      </span>

                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        {request.createdAt ? formatDate(request.createdAt) : "—"}
                      </span>

                      <KeyboardArrowRight
                        sx={{ fontSize: 20 }}
                        className="hidden md:block text-slate-300 group-hover:text-teal-600"
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {(data?.totalPages || 0) > 1 ? (
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            className="text-sm font-semibold text-slate-600 disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span className="text-xs text-slate-500">
            Page {page + 1} of {data.totalPages}
          </span>
          <button
            type="button"
            className="text-sm font-semibold text-slate-600 disabled:opacity-40"
            disabled={page + 1 >= data.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      ) : null}

      <UploadDocumentsDialog
        open={dialog === "upload"}
        onClose={() => setDialog(null)}
        onCreated={onCreated}
      />
      <RequestFromClientDialog
        open={dialog === "request"}
        onClose={() => setDialog(null)}
        onCreated={onCreated}
      />
    </div>
  );
};

export default DocumentsPage;
